<template>
  <div
    ref="$el"
    :class="[
      'hp-video',
      { '-has-button': hasButton },
      `-aspect-ratio-${aspectRatio}`,
      { '-video-not-ready': !ready && !iFrameVideo },
      { '-video-is-paused': !iFrameVideo && (paused || !playing) },
      { '-has-iframe': iFrameVideo },
    ]">
    <iframe
      v-if="iFrameVideo"
      :src="src"
      data-lenis-prevent
      frameborder="0" />

    <video
      v-else-if="src"
      ref="$player"
      loop
      :muted="muted"
      :autoplay="autoplay && !paused"
      :preload="paused ? 'none' : preload"
      :controls="controls"
      playsinline
      class="hp-video__item"
      :class="{ '-video-not-ready': !ready }"
      @loadeddata="ready = true"
      @play="handlePlay"
      @pause="handlePause">
      <source
        v-if="!isHLS"
        :src="src"
        type="video/mp4">
    </video>

    <!-- Elemento LCP della homepage: reso in SSR come <picture> (avif/webp +
         width/height + focal point) invece dell'attributo poster nativo, che
         serve una sola copia e ignora fetchpriority.
         The poster and first video frame can differ in crop and exposure.
         Crossfading the overlay avoids a hard swap amplified by frosted glass. -->
    <base-image
      v-if="!iFrameVideo && placeholder && showPoster"
      :src="placeholder.src"
      :webp="placeholder.webp"
      :avif="placeholder.avif"
      :width="placeholder.width"
      :height="placeholder.height"
      :has-focus="placeholder.hasFocus"
      :focus="placeholder.focus"
      alt=""
      aria-hidden="true"
      :lazy="false"
      fetchpriority="high"
      decoding="sync"
      class="hp-video__poster"
      :class="{ '-fade': fadePoster }"
      @transitionend="handlePosterTransitionEnd" />
  </div>
</template>

<script setup>
  const props = defineProps({
    src: {
      type: String,
      required: true
    },
    poster: [ Object, Array ],
    lazy: {
      type: Boolean,
      default: false
    },
    // 'none' lets the poster win LCP: the browser won't fetch the stream until
    // JS starts it. Combined with the paused-gating below, only the device's
    // active <video> ever downloads (the hidden one stays paused → no bytes).
    preload: {
      type: String,
      default: 'auto'
    },
    muted: {
      type: Boolean,
      default: true
    },
    paused: {
      type: Boolean,
      default: false
    },
    autoplay: {
      type: Boolean,
      default: true
    },
    controls: Boolean,
    aspectRatio: {
      type: String,
      default: 'rectangle',
      validator: val => [ 'rectangle', 'square' ].includes(val)
    },
    hasButton: Boolean
  })

  const isHLS = props.src.includes('.m3u8')
  const placeholder = getFirst(props.poster)

  const loaded = ref(!props.lazy)
  // A non-lazy MP4 is paintable from SSR HTML through muted autoplay (il
  // poster è già dipinto sopra da <base-image>). Hiding it until hydration
  // would leave the hero gray.
  const ready = ref(!props.lazy && !isHLS)
  const playing = ref(false)
  const isPaused = ref(false)
  const showPoster = ref(!!placeholder)
  const fadePoster = ref(false)

  const $el = ref(null)
  const $player = ref(null)
  let _hls = null
  let _uw = null

  const emit = defineEmits([ 'video-play', 'video-pause' ])

  const iFrameVideo = computed(() => {
    return !isHLS && (!props.src.includes('.mp4') && !props.src.includes('.webm'))
  })

  const revealVideo = () => {
    if (showPoster.value) {
      fadePoster.value = true
    }
  }

  const handlePosterTransitionEnd = event => {
    if (event.propertyName !== 'opacity') { return }
    showPoster.value = false
  }

  onMounted(() => {
    // Signal visual stability only after the first real video frame replaces
    // the poster. The hero uses this to enable expensive live backdrop filters.
    const player = $player.value
    if (player) {
      if (player.requestVideoFrameCallback) {
        player.requestVideoFrameCallback(revealVideo)
      } else {
        const onTime = () => {
          if (player.currentTime > 0) {
            revealVideo()
            player.removeEventListener('timeupdate', onTime)
          }
        }
        player.addEventListener('timeupdate', onTime)
      }
    }

    watch(() => props.paused, async nval => {
      if (nval && playing.value) {
        pause()
      } else {
        play()
      }
    })

    _uw = watch(loaded, async nval => {
      if (nval) {
        if (isHLS) {
          // hls.js caricato solo quando serve: l'import top-level renderebbe
          // il componente async, sospendendo l'hydration anche per gli mp4.
          const { default: HlsUtils } = await import('~/utils/hls')
          if (!$player.value) { return }
          _hls = new HlsUtils($player.value, props.src)

          if (loaded.value && props.autoplay && !props.paused) { play() }

          if (_uw) { _uw() }
        } else {
          ready.value = true
          if (loaded.value && props.autoplay && !props.paused) { play() }
          if (_uw) { _uw() }
        }
      }
    }, { immediate: true })
  })

  onUnmounted(() => {
    _hls?.destroy()
    ready.value = false
  })

  const handlePlayer = async isVisible => {
    await nextTick()

    if (props.lazy && !loaded.value && isVisible) {
      loaded.value = true
    } else if (ready.value && props.autoplay) {
      if (isVisible && !props.paused) {
        play()
      } else {
        pause()
      }
    }
  }

  // Explicit values, not toggles: the paused watcher and the <video> event both land here
  const handlePlay = () => {
    emit('video-play')
    playing.value = true
    isPaused.value = false
  }

  const handlePause = () => {
    emit('video-pause')
    playing.value = false
    isPaused.value = true
  }

  watch(() => props.paused, nval => {
    if (nval) {
      handlePause()
    } else {
      handlePlay()
    }
  })

  const play = () => {
    emit('video-play')
    // play() returns a promise that rejects with AbortError when a pause() lands
    // before playback starts (happens when the device track resolves right after
    // mount). It's benign — swallow it so it doesn't spam the console.
    const p = $player.value?.play()
    if (p && typeof p.catch === 'function') { p.catch(() => {}) }
  }
  const pause = () => {
    emit('video-pause')
    $player.value?.pause()
  }

  const isVisible = useElementVisibility($el, {
    rootMargin: '-10px -10px -10px -10px'
  })

  watch(isVisible, handlePlayer)
</script>

<style lang="scss" scoped>
  .hp-video {
    display: block;
    width: 100%;
    height: 100%;
    position: relative;

    &.-has-iframe {
      // height: 0;
      height: 100%;
      width: 100%;
      object-fit: cover;

      &::after { display: none; }
    }

    &.-aspect-ratio-rectangle.-video-not-ready {
      height: 0;
      padding-bottom: calc((9 / 16) * 100%);
    }

    &.-aspect-ratio-square.-video-not-ready {
      height: 0;
      padding-bottom: calc((5 / 4) * 100%);
    }

    &.-aspect-ratio-square { aspect-ratio: 4 / 4; }

    iframe {
      width: 100%;
      height: 100%;
      position: absolute;
      top: 0;
      left: 0;

      button { pointer-events: all; }
    }

    &__item {
      width: 100%;
      height: 100%;
      object-fit: cover;
      position: relative;

      &.-video-not-ready {
        position: absolute;
        height: 0;
        opacity: 0;
        inset: 0;
      }
    }

    &__poster.image {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      transition: opacity 0.6s ease;
      will-change: opacity; // Keep the crossfade on the compositor.

      &.-fade {
        opacity: 0;
      }
    }
  }
</style>
